import {
  AAD_PASSWORD_PREFIX,
  KEY_SIZE,
  NONCE_SIZE,
  PASSWORD_SALT_SIZE,
  PBKDF2_ITERATIONS,
  TRANSFER_ID_RE,
  WRAPPED_KEY_SIZE,
} from "./constants.js";
import { concat, randomBytes, utf8 } from "./bytes.js";
import { LscError } from "./errors.js";
import { importAesKey } from "./keys.js";

/**
 * Password → 32-byte KEK. The web client injects an Argon2id implementation (WASM);
 * this package ships the WebCrypto PBKDF2 fallback (§2, §7.3).
 * Implementations MUST NFC-normalize the password before hashing.
 */
export type PasswordKdf = (password: string, salt: Uint8Array) => Promise<Uint8Array>;

export const pbkdf2Kdf: PasswordKdf = async (password, salt) => {
  assertSalt(salt);
  const base = await crypto.subtle.importKey("raw", utf8(password.normalize("NFC")), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations: PBKDF2_ITERATIONS },
    base,
    KEY_SIZE * 8,
  );
  return new Uint8Array(bits);
};

export function generatePasswordSalt(): Uint8Array {
  return randomBytes(PASSWORD_SALT_SIZE);
}

function passwordAad(transferId: string): Uint8Array {
  if (!TRANSFER_ID_RE.test(transferId)) throw new LscError("BAD_INPUT", "invalid transferId");
  return utf8(AAD_PASSWORD_PREFIX + transferId);
}

/** §7.3 — WMK = nonce(12) || AES-256-GCM(KEK, nonce, MK, AAD = "LSC1/pw/" || transferId) → 60 bytes */
export async function wrapMasterKey(masterKey: Uint8Array, kek: Uint8Array, transferId: string): Promise<Uint8Array> {
  return _wrapMasterKeyWithNonce(masterKey, kek, transferId, randomBytes(NONCE_SIZE));
}

/** Test-only deterministic variant. */
export async function _wrapMasterKeyWithNonce(
  masterKey: Uint8Array,
  kek: Uint8Array,
  transferId: string,
  nonce: Uint8Array,
): Promise<Uint8Array> {
  if (masterKey.length !== KEY_SIZE) throw new LscError("BAD_INPUT", "masterKey must be 32 bytes");
  if (nonce.length !== NONCE_SIZE) throw new LscError("BAD_INPUT", "nonce must be 12 bytes");
  const k = await importAesKey(kek, ["encrypt"]);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, additionalData: passwordAad(transferId), tagLength: 128 },
      k,
      masterKey,
    ),
  );
  return concat(nonce, ct);
}

/** Inverse of wrapMasterKey. Wrong password and corrupted data are indistinguishable by design. */
export async function unwrapMasterKey(wrapped: Uint8Array, kek: Uint8Array, transferId: string): Promise<Uint8Array> {
  if (wrapped.length !== WRAPPED_KEY_SIZE) throw new LscError("BAD_INPUT", "wrapped key must be 60 bytes");
  const k = await importAesKey(kek, ["decrypt"]);
  try {
    const mk = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: wrapped.subarray(0, NONCE_SIZE), additionalData: passwordAad(transferId), tagLength: 128 },
      k,
      wrapped.subarray(NONCE_SIZE),
    );
    return new Uint8Array(mk);
  } catch {
    throw new LscError("WRONG_PASSWORD_OR_CORRUPT");
  }
}

function assertSalt(salt: Uint8Array): void {
  if (salt.length !== PASSWORD_SALT_SIZE) throw new LscError("BAD_INPUT", "salt must be 16 bytes");
}
